import { useState } from "react";
import { Card, CardContent } from "@/common/components/ui/card";
import { Input } from "@/common/components/ui/input";
import { Button } from "@/common/components/ui/button";
import { Search, ArrowLeft, Download, UserCheck } from "lucide-react";
import AntDTable from "@/common/components/ui/antd-table";
import { useNavigate, useParams } from "react-router-dom";
import finEaseHomeNavigationRoutes from "../../../../utils/navigationRoutes";

const AllotmentDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(
    {}
  );

  const handleColumnFilterChange = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
  };

  // Mock company name based on the ID (mocking detail fetch)
  const companyNames: Record<string, string> = {
    "1": "Bharat Coking Coal Limited",
    "2": "ICICI Prudential Asset Management Company Limited",
  };
  const companyName = companyNames[id ?? "1"] ?? "Bharat Coking Coal Limited";

  // Mock data for the detailed table
  const mockDetails = [
    {
      id: "101",
      company_name: companyName,
      client_code: "K123",
      client_name: "KUMUDBEN BHARATBHAI SHAH",
      mobile_no: "9824471361",
      active: "Y",
      dp_id: "100387",
    },
    {
      id: "102",
      company_name: companyName,
      client_code: "S0446",
      client_name: "SWETA RAJNIKANT PATEL",
      mobile_no: "9825062507",
      active: "Y",
      dp_id: "107358",
    },
    {
      id: "103",
      company_name: companyName,
      client_code: "S213",
      client_name: "SANJAY GUPTA HUF",
      mobile_no: "9824047499",
      active: "Y",
      dp_id: "102769",
    },
    {
      id: "104",
      company_name: companyName,
      client_code: "K663",
      client_name: "KETAN PRAKASHCHANDRA SHAH HUF",
      mobile_no: "9638787872",
      active: "Y",
      dp_id: "100004",
    },
    {
      id: "105",
      company_name: companyName,
      client_code: "S708",
      client_name: "SURESHKUMAR MATHURBHAI PARMAR",
      mobile_no: "",
      active: "N",
      dp_id: "105970",
    },
    {
      id: "106",
      company_name: companyName,
      client_code: "R058",
      client_name: "RAJIV S SHAH",
      mobile_no: "9712633788",
      active: "Y",
      dp_id: "108259",
    },
  ];

  const filteredDetails = mockDetails.filter(item => {
    const matchesSearch =
      item.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.client_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesColumnFilters = Object.entries(columnFilters).every(
      ([key, value]) => {
        if (!value) {
          return true;
        }
        return String(item[key as keyof typeof item])
          .toLowerCase()
          .includes(value.toLowerCase());
      }
    );
    return matchesSearch && matchesColumnFilters;
  });

  const handleExport = () => {
    // Logic for export would go here
  };

  const columns = [
    {
      title: "COMPANY NAME",
      dataIndex: "company_name",
      key: "company_name",
      width: 280,
      className: "text-sm text-slate-700 font-medium",
      sorter: (a: any, b: any) => a.company_name.localeCompare(b.company_name),
    },
    {
      title: "CLIENT CODE",
      dataIndex: "client_code",
      key: "client_code",
      width: 150,
      className: "text-sm font-bold text-[#0070c0]",
      sorter: (a: any, b: any) => a.client_code.localeCompare(b.client_code),
    },
    {
      title: "CLIENT NAME",
      dataIndex: "client_name",
      key: "client_name",
      width: 250,
      className: "text-sm text-slate-800 font-semibold",
      sorter: (a: any, b: any) => a.client_name.localeCompare(b.client_name),
    },
    {
      title: "MOBILE NO.",
      dataIndex: "mobile_no",
      key: "mobile_no",
      width: 160,
      className: "text-sm font-mono text-slate-600",
      render: (text: string) => text || "—",
    },
    {
      title: "ACTIVE",
      dataIndex: "active",
      key: "active",
      width: 100,
      align: "center" as const,
      render: (value: string) => (
        <div
          className={`inline-block rounded px-2 py-1 text-xs font-bold ${value === "Y" ? "border border-emerald-100 bg-emerald-50 text-emerald-600" : "border border-rose-100 bg-rose-50 text-rose-500"}`}
        >
          {value === "Y" ? "YES" : "NO"}
        </div>
      ),
    },
    {
      title: "DP ID",
      dataIndex: "dp_id",
      key: "dp_id",
      width: 160,
      className: "text-sm font-mono text-slate-600",
    },
  ];

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6">
      {/* Enhanced Detail Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 shadow-xl shadow-primary/10 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />

        <div className="relative flex flex-col justify-between gap-6 md:flex-row md:items-center">
          <div className="flex items-center gap-5">
            <div className="rounded-xl border border-white/10 bg-white/20 p-3 shadow-inner backdrop-blur-md">
              <UserCheck className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold leading-tight tracking-tight text-white md:text-3xl">
                {companyName}
              </h1>
              <p className="font-medium text-white/80">
                Review detailed client distribution and allotment status
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              className="h-11 gap-2 border-white/20 bg-white/10 text-white shadow-sm backdrop-blur-sm hover:bg-white/20"
              onClick={handleExport}
            >
              <Download className="h-4 w-4" />
              Export Data
            </Button>
            <Button
              variant="default"
              onClick={() =>
                navigate(finEaseHomeNavigationRoutes.IPO_ALLOTMENT)
              }
              className="h-11 gap-2 bg-white px-8 font-bold text-secondary shadow-lg shadow-white/5 hover:bg-white/90"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to List
            </Button>
          </div>
        </div>
      </div>

      {/* Results Info & Search Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-2">
        <div className="flex items-center gap-3">
          <p className="text-sm font-medium text-slate-500">
            Total Clients:{" "}
            <span className="font-bold text-slate-900">
              {filteredDetails.length}
            </span>
          </p>
        </div>
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by Client Code or Name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="h-10 rounded-lg border-slate-200 bg-white pl-10 font-medium shadow-sm focus:ring-primary/10"
          />
        </div>
      </div>

      {/* Detailed Data Table */}
      <Card className="overflow-hidden rounded-2xl border-slate-200 bg-white shadow-xl shadow-slate-200/40">
        <CardContent className="p-0">
          <div className="custom-scrollbar overflow-x-auto">
            <AntDTable
              columns={columns}
              data={filteredDetails}
              pagination={false}
              rowKey="id"
              scroll={{ x: 1300 }}
              showSettings
              showColumnFilter
              onFilterChange={handleColumnFilterChange}
              sticky
              className="details-table-enhanced"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AllotmentDetails;
